/**
 * Periodic poll for transaction/book/category changes from another user
 * (typically a household member writing to the same shared spreadsheet).
 *
 * Why polling: Google Sheets has no push/webhook for client-side apps.
 * The cheapest delta-detection is a periodic re-list. The Sheets API
 * `valuesGet` for a single tab is small (~KB-scale for typical household
 * data) so a 30 s interval is well within the 100 req/100 s/user quota.
 *
 * Visibility-aware: the poll suspends while the tab is hidden (saves
 * battery on mobile, avoids burning quota when nobody's looking) and
 * fires immediately on `visibilitychange → visible` so a user returning
 * to the tab sees fresh data without waiting up to 30 s.
 *
 * Race-safety: we never overwrite local pending writes. Each row in the
 * server payload is merged via id-keyed `upsert`; if a row also exists
 * in the local sync queue (`pending` set), the server's older `updated_at`
 * is dropped. Without this, polling could clobber a row mid-edit.
 *
 * Sharp edges:
 *   - The poll handler is created via `startRealtimePoll(...)` and returns
 *     a `stop()` function — caller MUST call it on logout / page unload
 *     to avoid leaked timers + ghost reads on a stale spreadsheet id.
 *   - First fire is `INTERVAL_MS` after start, NOT immediate — initial
 *     load already happens via `loadInitial` in App's bootstrap.
 */

import type { SheetsClient } from './client';
import { MissingTokenError } from './client';
import { booksRepo } from './repositories/books';
import { categoriesRepo } from './repositories/categories';
import { transactionsRepo } from './repositories/transactions';
import { useStore } from '@/state/store';
import type { TransactionRow, BookRow, CategoryRow } from './types';

export const POLL_INTERVAL_MS = 30_000;

export interface RealtimePollHandle {
  stop: () => void;
  /** Force one fetch + merge cycle now (used by tests). */
  pulse: () => Promise<void>;
}

interface StartOptions {
  client: SheetsClient;
  spreadsheetId: string;
  intervalMs?: number;
}

/**
 * Merge `incoming` into `current` by id, preferring whichever side has
 * the newer `updated_at`. Rows in `pendingIds` are NEVER overwritten —
 * those have unflushed local writes that would lose user input.
 */
function mergeById<T extends { id: string; updated_at?: string }>(
  current: readonly T[],
  incoming: readonly T[],
  pendingIds: ReadonlySet<string>,
): T[] {
  const byId = new Map<string, T>();
  for (const r of current) byId.set(r.id, r);
  for (const r of incoming) {
    if (pendingIds.has(r.id)) continue; // local write wins
    const existing = byId.get(r.id);
    if (!existing) {
      byId.set(r.id, r);
      continue;
    }
    const existingTs = existing.updated_at ?? '';
    const incomingTs = r.updated_at ?? '';
    if (incomingTs >= existingTs) byId.set(r.id, r);
  }
  return [...byId.values()];
}

function pendingTxIds(): Set<string> {
  const set = new Set<string>();
  const ops = useStore.getState().sync.pending;
  for (const op of ops) {
    const p = op.payload as { id?: string } | null | undefined;
    if (p && typeof p.id === 'string') set.add(p.id);
  }
  return set;
}

export function startRealtimePoll(opts: StartOptions): RealtimePollHandle {
  const { client, spreadsheetId } = opts;
  const intervalMs = opts.intervalMs ?? POLL_INTERVAL_MS;
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  let stopped = false;

  const tick = async (): Promise<void> => {
    if (stopped || inFlight) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    inFlight = true;
    try {
      const [books, categories, transactions] = await Promise.all([
        booksRepo.list(client, spreadsheetId),
        categoriesRepo.list(client, spreadsheetId),
        transactionsRepo.list(client, spreadsheetId),
      ]);
      const pending = pendingTxIds();
      const s = useStore.getState();
      const mergedBooks = mergeById<BookRow>(s.books.items, books, pending);
      const mergedCats = mergeById<CategoryRow>(s.categories.items, categories, pending);
      const mergedTx = mergeById<TransactionRow>(s.transactions.items, transactions, pending);
      // setItems also re-resolves activeBookId; safe to call repeatedly.
      s.books.setItems(mergedBooks);
      s.categories.setItems(mergedCats);
      s.transactions.setItems(mergedTx);
    } catch (err) {
      // Missing-token failures mean the user logged out (in this tab or
      // another) or the 1-hour OAuth window expired. Re-firing every 30s
      // just spams the console; bail the loop so the next bootstrap (on
      // re-login / token refresh) starts a fresh poll.
      if (err instanceof MissingTokenError) {
        // eslint-disable-next-line no-console
        console.info('[Mochia/realtimePoll] stopping — auth token gone');
        stopped = true;
        if (timerId !== null) {
          clearTimeout(timerId);
          timerId = null;
        }
        return;
      }
      // Other failures (Sheets quota / network blip / 5xx) are
      // best-effort — log once per tick, keep going.
      // eslint-disable-next-line no-console
      console.warn('[Mochia/realtimePoll] tick failed', err);
    } finally {
      inFlight = false;
    }
  };

  const schedule = (): void => {
    if (stopped) return;
    timerId = setTimeout(() => {
      void (async (): Promise<void> => {
        await tick();
        schedule();
      })();
    }, intervalMs);
  };

  const onVisibility = (): void => {
    if (stopped) return;
    if (typeof document === 'undefined') return;
    if (!document.hidden) void tick();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  schedule();

  return {
    stop: () => {
      stopped = true;
      if (timerId !== null) clearTimeout(timerId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    },
    pulse: tick,
  };
}
